import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Button } from '@campgladiator/cgui-core.atoms.button'
import { Card } from '@campgladiator/cgui-core.atoms.card'
import { Loader } from '@campgladiator/cgui-core.atoms.loader'
import { Heading, Paragraph } from '@campgladiator/cgui-core.atoms.typography'
import { HealthStatsSuccessMsg } from '../'
import { LabelInput, LabelTimeInput } from '../../../common/components'
import {
  preventNonNumericalInput,
  statFormLimits,
  getDigitValidation,
  errorMessage,
  STATS_SUBMISSION_ERROR,
  convertMinAndSecToSec,
} from '../../../common/utility'
import { updateChallengeStats } from '../../../redux/features/challengeActions'

const config = process.env
const initialStatsFormData = {
  bodyWeight: null,
  bodyFat: null,
  muscleMass: null,
  mileTimeMin: null,
  mileTimeSec: null,
  squats: null,
  sitUps: null,
  pushUps: null,
}
const initialStatsFormError = {
  bodyWeightError: false,
  bodyFatError: false,
  muscleMassError: false,
  mileTimeMinError: false,
  mileTimeSecError: false,
  squatsError: false,
  sitUpsError: false,
  pushUpsError: false,
}

const getErrorDetails = (statsFormError) =>
  Object.values(statsFormError).filter((e) => e === true).length

const setErrorMessage = (error) => (error ? errorMessage : error)

const HealthStatForm = ({ onSubmit = (isError) => {} }) => {
  const dispatch = useDispatch()
  const [statsFormData, setStatsFormData] = useState(initialStatsFormData)
  const [statsFormError, setStatsFormError] = useState(initialStatsFormError)
  const [showStatsForm] = useState(true)
  const [hasInput, setHasInput] = useState(false)
  const [bodyWeightInputFocus] = useState(0)
  const [statsSubmissionError, setStatsSubmissionError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const {
    bodyWeightError,
    bodyFatError,
    muscleMassError,
    mileTimeMinError,
    mileTimeSecError,
    squatsError,
    sitUpsError,
    pushUpsError,
  } = statsFormError

  const onInputChange = (e) => {
    const {
      target: { id, value },
    } = e

    if (!isNaN(value) && value.length) {
      let error = false
      let errorObj = {}
      if (id === 'mileTimeMin' || id === 'workoutTimeMin') {
        errorObj = statFormLimits.minutes
      } else if (id === 'mileTimeSec' || id === 'workoutTimeSec') {
        errorObj = statFormLimits.seconds
      } else {
        errorObj = statFormLimits[id]
      }
      const { min, max, maxDigits } = errorObj
      const newValue =
        getDigitValidation({ id, value, maxDigits }) || !value.length
          ? value
          : statsFormData[id]
      setStatsFormData({
        ...statsFormData,
        [id]: newValue,
      })
      error =
        newValue < min || newValue > max || Number(newValue) < min
          ? true
          : false
      setStatsFormError({ ...statsFormError, [`${id}Error`]: error })
    } else if (!value.length) {
      setStatsFormData({ ...statsFormData, [id]: null })
      setStatsFormError({ ...statsFormError, [`${id}Error`]: false })
    }
    setStatsSubmissionError(false)
  }

  const parseStats = () => {
    const { mileTimeMin, mileTimeSec } = statsFormData
    const mileTime =
      mileTimeMin && mileTimeSec
        ? convertMinAndSecToSec({ mileTimeMin, mileTimeSec })
        : null
    const statsToNumbers = Object.keys(statsFormData).reduce((acc, key) => {
      acc[key] = statsFormData[key] !== null ? Number(statsFormData[key]) : null
      return acc
    }, {})
    const parsedStats = {
      ...statsToNumbers,
      mileTime,
      mileTimeMin: undefined,
      mileTimeSec: undefined,
    }

    return parsedStats
  }

  const isAtLeastOneStatSupplied = useCallback(
    () => Object.values(statsFormData).some((value) => value !== null),
    [statsFormData],
  )

  const userStatsSubmissionHandler = async () => {
    setIsLoading(true)
    setStatsSubmissionError(false)
    const cleanStats = parseStats()
    try {
      await dispatch(updateChallengeStats(cleanStats))
      onSubmit(false)
    } catch {
      setStatsSubmissionError(true)
      onSubmit(true)
    } finally {
      resetStatsFormState()
      setIsLoading(false)
    }
  }

  const resetStatsFormState = () => {
    setStatsFormData(initialStatsFormData)
    setStatsFormError(initialStatsFormError)
  }

  const submitStats = async (e) => {
    e.preventDefault()
    await userStatsSubmissionHandler()
    e.target.reset()
  }

  useEffect(() => {
    setHasInput(isAtLeastOneStatSupplied())
  }, [isAtLeastOneStatSupplied])

  return (
    <Card className="stat-widget" raised>
      <div>
        <div className="stat-widget__header">
          <img
            src={`${config.REACT_APP_CDN_URL}dumbbells.svg`}
            alt=""
            className="stat-widget__header-img"
          />
          <div className="stat-widget__header-content">
            <Heading type="h4">Submit Your Stats</Heading>
            <Paragraph className="stat-widget__header-content-text">
              Submit your stats to track your progress from the start of the
              challenge.
            </Paragraph>
          </div>
        </div>
        {showStatsForm ? (
          <form className="stat-form" onSubmit={submitStats}>
            <div className="form__control-group">
              <LabelInput
                label="Body Weight:"
                placeholder="000.0"
                id="bodyWeight"
                type="number"
                unit="lbs"
                data-testid="weight"
                onKeyPress={preventNonNumericalInput}
                onChange={onInputChange}
                value={statsFormData.bodyWeight}
                error={setErrorMessage(bodyWeightError)}
                min={0}
                autoFocus={bodyWeightInputFocus === 1}
              />
              <LabelInput
                label="Body Fat:"
                placeholder="0%"
                id="bodyFat"
                type="number"
                unit="%"
                data-testid="bodyFat"
                onKeyPress={preventNonNumericalInput}
                onChange={onInputChange}
                value={statsFormData.bodyFat}
                error={setErrorMessage(bodyFatError)}
                min={0}
              />
              <LabelInput
                label="Muscle Mass:"
                placeholder="0.0"
                id="muscleMass"
                type="number"
                data-testid="muscleMass"
                onKeyPress={preventNonNumericalInput}
                onChange={onInputChange}
                value={statsFormData.muscleMass}
                error={setErrorMessage(muscleMassError)}
                min={0}
                step="0.1"
              />
            </div>
            <LabelTimeInput
              label="Mile Time:"
              minPlaceholder="mm"
              secPlaceholder="ss"
              minUnit="min"
              secUnit="sec"
              minId="mileTimeMin"
              secId="mileTimeSec"
              type="number"
              onKeyPress={preventNonNumericalInput}
              onChange={onInputChange}
              minValue={statsFormData.mileTimeMin}
              secValue={statsFormData.mileTimeSec}
              minError={setErrorMessage(mileTimeMinError)}
              secError={setErrorMessage(mileTimeSecError)}
              min={0}
            />
            <LabelInput
              label="Squats:"
              placeholder="0"
              id="squats"
              type="number"
              data-testid="squats"
              onKeyPress={preventNonNumericalInput}
              onChange={onInputChange}
              value={statsFormData.squats}
              error={setErrorMessage(squatsError)}
              min={0}
            />
            <LabelInput
              label="Push Ups:"
              placeholder="0"
              id="pushUps"
              type="number"
              data-testid="pushUps"
              onKeyPress={preventNonNumericalInput}
              onChange={onInputChange}
              value={statsFormData.pushUps}
              error={setErrorMessage(pushUpsError)}
              min={0}
            />
            <LabelInput
              label="Sit Ups:"
              placeholder="0"
              id="sitUps"
              type="number"
              data-testid="sitUps"
              onKeyPress={preventNonNumericalInput}
              onChange={onInputChange}
              value={statsFormData.sitUps}
              error={setErrorMessage(sitUpsError)}
              min={0}
            />
            <div className="stat-form__btn-wrap">
              <Button
                primary
                solid
                className="form__btn-submit stat-form__btn-submit"
                type="submit"
                disabled={
                  getErrorDetails(statsFormError) || !hasInput || isLoading
                }
              >
                {isLoading ? <Loader emphasis="negative" /> : 'submit my stats'}
              </Button>
            </div>

            {statsSubmissionError && (
              <Paragraph className="stat-form__error">
                {STATS_SUBMISSION_ERROR}
              </Paragraph>
            )}
          </form>
        ) : (
          <HealthStatsSuccessMsg />
        )}
      </div>
    </Card>
  )
}

export default HealthStatForm
